import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { SidebarProvider } from '@/contexts/SidebarContext';
import AppSidebar from '@/components/AppSidebar';
import DashboardFooter from '@/components/DashboardFooter';
import { useSidebarContext } from '@/contexts/SidebarContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const WEBHOOK_URL = 'https://growtika.app.n8n.cloud/webhook/file-upload';

const FactCheckLayout: React.FC = () => {
  const { compact } = useSidebarContext();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [poppedOutFacts, setPoppedOutFacts] = useState<Set<number>>(new Set());
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');

  const handleNavigate = (page: string) => {
    switch (page) {
      case 'dashboard':
      case 'deep-analysis':
      case 'recommended':
      case 'faq-serp':
        navigate('/dashboard');
        break;
      case 'eco':
        navigate('/eco');
        break;
      case 'fact-check':
        break;
      default:
        console.log('Unknown navigation target:', page);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setMessage('No file selected.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('Uploading file and processing data...');
    setMessageType('info');
    setData([]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setMessage('File uploaded and data received. Rendering table...');
        setMessageType('success');
        setData(result);
      } else {
        const text = await response.text();
        setMessage(`Failed: ${response.status} - ${text}`);
        setMessageType('error');
      }
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTextSubmit = async () => {
    if (!textInput.trim()) {
      setMessage("Please enter some text.");
      setMessageType("error");
      return;
    }
    setLoading(true);
    setMessage("Sending text for analysis...");
    setMessageType("info");
    setData([]);
    setPoppedOutFacts(new Set()); // Reset popped out facts
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textInput })
      });
      const result = await res.json();
      console.log('Text analysis result:', result); // Debug log
      setMessage('Text analyzed successfully.');
      setMessageType('success');
      setData(result);
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) {
      setMessage("Please enter a valid URL.");
      setMessageType("error");
      return;
    }
    setLoading(true);
    setMessage("Sending URL for processing...");
    setMessageType("info");
    setData([]);
    setPoppedOutFacts(new Set()); // Reset popped out facts
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput })
      });
      const result = await res.json();
      setMessage('URL analyzed successfully.');
      setMessageType('success');
      setData(result);
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const getRowClass = (item: any) => {
    if (item.Yes_Count === 3) return 'bg-green-50 border-green-200';
    if (item.Yes_Count === 0 && item.No_Count > 0) return 'bg-red-50 border-red-200';
    if (item.Yes_Count > 0 && item.No_Count > 0) return 'bg-yellow-50 border-yellow-200';
    if (item.Yes_Count > 0 && item.No_Count === 0) return 'bg-green-50 border-green-200';
    return 'bg-muted/30';
  };

  const getResultIcon = (item: any) => {
    if (item.Yes_Count === 3) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (item.Yes_Count === 0 && item.No_Count > 0) return <XCircle className="h-4 w-4 text-red-600" />;
    if (item.Yes_Count > 0 && item.No_Count > 0) return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    if (item.Yes_Count > 0 && item.No_Count === 0) return <CheckCircle className="h-4 w-4 text-green-600" />;
    return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
  };

  const getResultText = (item: any) => {
    if (item.Yes_Count === 3) return 'Verified';
    if (item.Yes_Count === 0 && item.No_Count > 0) return 'Disputed';
    if (item.Yes_Count > 0 && item.No_Count > 0) return 'Mixed Evidence';
    if (item.Yes_Count > 0 && item.No_Count === 0) return 'Verified';
    return 'Pending Review';
  };

  const handlePopOutFact = (index: number) => {
    setPoppedOutFacts(prev => new Set([...prev, index]));
  };

  const filteredData = data.filter((_, index) => !poppedOutFacts.has(index));

  return (
    <div className="flex min-h-screen w-full bg-gradient-to-br from-background via-background to-secondary/20">
      <AppSidebar activePage="fact-check" onNavigate={handleNavigate} />
      <div className={cn("flex-1 min-w-0 transition-all duration-300", !isMobile && (compact ? "ml-16" : "ml-64"))}>
        <div className="container mx-auto p-6 space-y-8">
          
          {/* Header Section */}
          <div className="text-center space-y-4 py-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <FileText className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Fact Check Analysis
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Upload a file, paste content, or enter a URL to verify facts and get detailed analysis
            </p>
          </div>

          {/* Input Options Tabs */}
          <Tabs defaultValue="file" className="w-full max-w-2xl mx-auto">
            <TabsList className="grid grid-cols-3 mb-6">
              <TabsTrigger value="file">Upload PDF</TabsTrigger>
              <TabsTrigger value="text">Paste Text</TabsTrigger>
              <TabsTrigger value="url">Enter URL</TabsTrigger>
            </TabsList>

            <TabsContent value="file">
              <Card className="shadow-lg border-0 bg-card/80 backdrop-blur-sm">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="flex items-center gap-2 justify-center">
                    <Upload className="h-5 w-5 text-primary" />
                    Upload PDF Document
                  </CardTitle>
                  <CardDescription>
                    Select a PDF file to analyze facts and verify statements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        handleFileChange(e);
                        e.target.value = '';
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      disabled={loading}
                    />
                    <div className="border-2 border-dashed border-primary/30 rounded-lg p-8 text-center hover:border-primary/50 transition-colors bg-primary/5">
                      <Upload className="h-12 w-12 text-primary mx-auto mb-4" />
                      <p className="text-lg font-medium text-foreground mb-2">
                        {loading ? 'Processing...' : 'Choose PDF file to upload'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Drag and drop or click to select
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="text">
              <Card className="shadow-lg border-0 bg-card/80 backdrop-blur-sm">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="flex items-center gap-2 justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                    Paste Text Content
                  </CardTitle>
                  <CardDescription>
                    Enter or paste text content to analyze facts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    rows={8}
                    className="w-full p-4 border rounded-md resize-none bg-background text-foreground"
                    placeholder="Paste your text content here..."
                    disabled={loading}
                  />
                  <Button 
                    onClick={handleTextSubmit} 
                    disabled={loading || !textInput.trim()}
                    className="w-full"
                  >
                    {loading ? 'Analyzing...' : 'Analyze Text'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="url">
              <Card className="shadow-lg border-0 bg-card/80 backdrop-blur-sm">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="flex items-center gap-2 justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                    Analyze URL Content
                  </CardTitle>
                  <CardDescription>
                    Enter a public URL to scrape and verify facts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="w-full p-3 border rounded-md bg-background text-foreground"
                    placeholder="https://example.com/article"
                    disabled={loading}
                  />
                  <Button 
                    onClick={handleUrlSubmit} 
                    disabled={loading || !urlInput.trim()}
                    className="w-full"
                  >
                    {loading ? 'Analyzing...' : 'Analyze URL'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Loading and Message Display */}
          {loading && (
            <div className="flex items-center justify-center gap-3 py-4">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-muted-foreground">Processing your content...</span>
            </div>
          )}

          {message && (
            <div className={cn(
              "p-4 rounded-lg flex items-center gap-2 animate-fade-in max-w-2xl mx-auto",
              messageType === 'success' && "bg-green-50 text-green-700 border border-green-200",
              messageType === 'error' && "bg-red-50 text-red-700 border border-red-200",
              messageType === 'info' && "bg-blue-50 text-blue-700 border border-blue-200"
            )}>
              {messageType === 'success' && <CheckCircle className="h-4 w-4" />}
              {messageType === 'error' && <XCircle className="h-4 w-4" />}
              {messageType === 'info' && <AlertCircle className="h-4 w-4" />}
              <span className="font-medium">{message}</span>
            </div>
          )}

          {/* Results Section */}
          {data.length > 0 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground mb-2">Analysis Results</h2>
                <p className="text-muted-foreground">
                  {data.length} statements analyzed and verified
                </p>
              </div>

              <div className="grid gap-4">
                {filteredData.map((item, originalIndex) => {
                  const index = data.indexOf(item);
                  return (
                    <Card 
                      key={index} 
                      className={cn(
                        "transition-all duration-300 hover:shadow-md border-l-4 animate-fade-in",
                        getRowClass(item)
                      )}
                    >
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Statement with Pop Out Button */}
                          <div className="space-y-2">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                {getResultIcon(item)}
                              </div>
                              <h3 className="font-medium text-foreground leading-relaxed flex-1">
                                {item.Statement}
                              </h3>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePopOutFact(index)}
                                className="flex-shrink-0 h-8 w-8 p-0 hover:bg-green-100 hover:text-green-700 transition-colors"
                                title="Mark as verified and remove"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Stats and Result */}
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                              {getResultIcon(item)}
                              <span className="font-semibold text-sm">
                                {getResultText(item)}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-600" />
                                <strong>{item.Yes_Count}</strong> verified
                              </span>
                              <span className="flex items-center gap-1">
                                <XCircle className="h-3 w-3 text-red-600" />
                                <strong>{item.No_Count}</strong> disputed
                              </span>
                            </div>
                          </div>

                          {/* Sources */}
                          {item.No_Sources && item.No_Sources.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-muted-foreground">Disputed Sources:</h4>
                              <div className="space-y-3">
                                {item.No_Sources.map((src: any, i: number) => (
                                  <div key={i} className="bg-muted/30 rounded-lg p-4 text-sm space-y-2">
                                    <div>
                                      <span className="font-medium text-foreground">{src.source}:</span>
                                      <span className="text-muted-foreground ml-2">
                                        {src.reason || 'No specific reason provided.'}
                                      </span>
                                    </div>
                                    {src.suggestion && (
                                      <div className="border-l-2 border-primary/30 pl-3 bg-primary/5 rounded-r-md py-2">
                                        <span className="text-xs font-medium text-primary uppercase tracking-wide">Suggested:</span>
                                        <p className="text-foreground mt-1">{src.suggestion}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FactCheck: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        <SidebarProvider>
          <FactCheckLayout />
        </SidebarProvider>
      </main>
      <DashboardFooter />
    </div>
  );
};

export default FactCheck;
